<i18n
  src="@/locales/views/agro_exporter/production/uniq_final_packing_list/edit_packages.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/package.yml" lang="yaml"></i18n>
<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name='routeName("show_container_packages")'
    :params="{
      agro_exporter_process_id: this.agro_exporter_process_id,
      container_id: this.container_id,
    }"
  />

  <container-header
    :agro_exporter_process_id="agro_exporter_process_id"
    :container_code="container?.container_code"
  />

  <div v-if="load_general_data" class="mt-8">
    <div class="panel">
      <div class="panel-header">
        <h3 class="panel-header-title">
          {{ $t("labels.title") }}
        </h3>
      </div>
      <div class="panel-body">
        <div class="space-y-8 divide-y divide-gray-200" v-if="new_package">
          <div
            class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 divide-y-2"
          >
            <div class="sm:col-span-6">
              <label class="label-form">
                {{ $t("model.package.producer") }}
              </label>
              <div class="content-data">{{ getProducerLabel() }}</div>
            </div>
            <div class="sm:col-span-6">
              <package-attrs
                v-if="load_general_data"
                :new_package="new_package"
                :exporter_process_crop_categories="
                  exporter_process_crop_categories
                "
                :exporter_process_crop_varieties="
                  exporter_process_crop_varieties
                "
                :exporter_process_crop_sizes="exporter_process_crop_sizes"
                :exporter_process_crop_packages="exporter_process_crop_packages"
                :exporter_process_brands="exporter_process_brands"
                v-model:quantity="new_package.quantity"
                v-model:exporter_process_crop_category_id="
                  new_package.exporter_process_crop_category_id
                "
                v-model:exporter_process_crop_variety_id="
                  new_package.exporter_process_crop_variety_id
                "
                v-model:exporter_process_crop_size_id="
                  new_package.exporter_process_crop_size_id
                "
                v-model:exporter_process_crop_package_id="
                  new_package.exporter_process_crop_package_id
                "
                v-model:exporter_process_brand_id="
                  new_package.exporter_process_brand_id
                "
              ></package-attrs>
              <package-group
                v-model:package_group_id="new_package.package_group_id"
                v-model:package_group:pg_code="
                  new_package.package_group.pg_code
                "
                v-model:package_group:thermo_code="
                  new_package.package_group.thermo_code
                "
                :package_item="new_package"
                :package_groups="package_groups"
              ></package-group>
              <div
                class="col-span-2 grid grid-cols-4 gap-x-2 sm:gap-x-4 gap-y-2 py-3"
              >
                <h3
                  v-if="new_package.exporter_process_crop_sizes.length > 1"
                  class="col-span-4 text-sm font-medium text-gray-700"
                >
                  {{ $t("model.package.exporter_process_crop_size") }}s
                </h3>
                <div
                  v-for="exporter_process_crop_size in new_package.exporter_process_crop_sizes"
                  :key="exporter_process_crop_size.id"
                  class="col-span-2 sm:col-span-1"
                >
                  <label class="block text-sm font-medium text-gray-700">
                    {{ exporter_process_crop_size.label }}</label
                  >
                  <input
                    type="text"
                    :value="exporter_process_crop_size.quantity"
                    @input="
                      exporter_process_crop_size.quantity = $event.target.value
                    "
                    class="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
              </div>
            </div>
            <div class="sm:col-span-6 text-right py-3 px-4 sm:px-8">
              <button
                :class="[
                  'btn-white',
                  { 'opacity-50 cursor-not-allowed': !validate }
                ]"
                :disabled="!validate"
                @click.prevent="addPackage()"
              >
                <i class="fa-solid fa-box pr-2"></i>
                {{ editing ? $t("labels.update_link") : $t("labels.add_link") }}
              </button>
            </div>
          </div>
        </div>
        <div class="mt-8 flex flex-col">
          <div class="table-first-container">
            <div class="table-second-container">
              <div class="table-third-container">
                <table class="table">
                  <thead class="table-header">
                    <tr>
                      <th scope="col" class="table-header-cell">
                        {{ $t("model.package.exporter_process_crop_category") }}
                      </th>
                      <th scope="col" class="table-header-cell">
                        {{ $t("model.package.exporter_process_crop_variety") }}
                      </th>
                      <th scope="col" class="table-header-cell">
                        {{ $t("model.package.exporter_process_brand") }}
                      </th>
                      <th scope="col" class="table-header-cell">
                        {{ $t("model.package.exporter_process_crop_package") }}
                      </th>
                      <th scope="col" class="table-header-cell">
                        {{ $t("model.package.package_group") }}
                      </th>
                      <th
                        scope="col"
                        v-for="exporter_process_crop_size in exporter_process_crop_sizes"
                        :key="exporter_process_crop_size.id"
                        class="table-header-cell"
                      >
                        {{ exporter_process_crop_size.name }}
                      </th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody class="table-body">
                    <tr
                      v-for="(package_item, index) in packages_visibles"
                      :key="index"
                      class="table-row"
                    >
                      <td class="table-cell">
                        {{
                          getCropCategoryLabel(
                            package_item.exporter_process_crop_category_id
                          )
                        }}
                      </td>
                      <td class="table-cell">
                        {{
                          getCropVarietyLabel(
                            package_item.exporter_process_crop_variety_id
                          )
                        }}
                      </td>
                      <td class="table-cell">
                        {{
                          getBrandLabel(package_item.exporter_process_brand_id)
                        }}
                      </td>
                      <td class="table-cell">
                        {{
                          $t(
                            "model.package.exporter_process_crop_package_value",
                            getCropPackageLabel(
                              package_item.exporter_process_crop_package_id
                            )
                          )
                        }}
                      </td>
                      <td class="table-cell">
                        {{
                          package_item.package_group_id
                            ? this.getPackageGroupLabel(
                                package_item.package_group_id
                              )
                            : package_item.package_group.pg_code
                        }}
                      </td>
                      <td
                        scope="col"
                        v-for="exporter_process_crop_size in exporter_process_crop_sizes"
                        :key="exporter_process_crop_size.id"
                        class="table-cell"
                      >
                        {{
                          package_item.exporter_process_crop_sizes.find(
                            exp_process_crop_size =>
                              exp_process_crop_size.id ==
                              exporter_process_crop_size.id
                          )?.quantity
                        }}
                      </td>

                      <td>
                        <i
                          class="fa-solid fa-trash text-red-500 pr-2 cursor-pointer justify-between"
                          @click="removePackage(package_item)"
                        ></i>
                        <i
                          class="fa-solid fa-pencil text-yellow-500"
                          @click="editPackage(package_item)"
                        ></i>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="panel-footer">
        <div class="panel-footer-actions">
          <white-button @click.prevent="goToShowContainer()" :label='$t("labels.cancel_button")' />
          <span class="space-actions"></span>
          <success-button v-if="!proc_save_button" @click.prevent="update()" :label="$t('labels.save_button')" />
          <success-button-processing v-else :label="$t('labels.save_button')" />
        </div>
      </div>
    </div>
  </div>
  <loading-message v-else />
</template>

<script>
import Requester from "@/views/agro_exporter/requesters/production/uniq_final_packing_list/edit_packages.js";
import PackageAttrs from "@/components/views/agro_exporter/production/uniq_final_packing_list/show_container/tabs/packages/PackageAttrs2.vue";
import PackageGroup from "@/components/views/agro_exporter/production/uniq_final_packing_list/show_container/tabs/packages/PackageGroup.vue";
import ContainerHeader from "@/components/views/agro_exporter/production/uniq_final_packing_list/ContainerHeader.vue";

export default {
  name: "EditPackages",
  components: {
    PackageAttrs,
    PackageGroup,
    ContainerHeader
  },
  data() {
    return {
      load_general_data: false,
      proc_save_button: false,
      container: null,
      producer_organization_selected: null,
      producers_organizations: [],
      packages: [],
      package_groups: [],
      exporter_process_crop_categories: [],
      exporter_process_crop_varieties: [],
      exporter_process_crop_sizes: [],
      exporter_process_crop_packages: [],
      exporter_process_brands: [],
      show_label: false,
      new_package: null,
      editing: false,
      producer_name: null,
      organization_name: null,
      max_package_groups: 0,
      len_package_groups: 0
    };
  },
  props: {
    agro_exporter_id: { required: true },
    agro_exporter_process_id: {
      required: true
    },
    container_id: {
      required: true
    },
    producer_id: {},
    organization_id: {}
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          agro_exporter_id: this.agro_exporter_id
        }
      }).then(() => {
        this.getContainer();
        this.loadData();
      })
    );
  },
  computed: {
    packages_visibles() {
      return this.packages.filter(
        package_item => package_item._destroy == false
      );
    },
    validate: function () {
      if (!this.new_package) return false;
      if (
        this.new_package.exporter_process_crop_package_id &&
        this.new_package.exporter_process_crop_category_id &&
        this.new_package.exporter_process_crop_variety_id &&
        this.new_package.exporter_process_brand_id &&
        (this.new_package.package_group_id ||
          this.new_package.package_group.pg_code) &&
        this.new_package.exporter_process_crop_sizes.filter(
          exp_process_crop_size => Number(exp_process_crop_size.quantity) > 0
        ).length > 0
      )
        return true;
      return false;
    },
    packages_params: function () {
      let packages_params = [];
      this.packages.forEach(package_item => {
        package_item.exporter_process_crop_sizes
          .filter(exp_pr_crop_size => Number(exp_pr_crop_size.quantity) > 0)
          .forEach(pkg_crop_size => {
            packages_params.push({
              id: package_item.id,
              producer_id: this.producer_id,
              organization_id: this.organization_id,
              exporter_process_crop_package_id:
                package_item.exporter_process_crop_package_id,
              exporter_process_crop_category_id:
                package_item.exporter_process_crop_category_id,
              exporter_process_crop_variety_id:
                package_item.exporter_process_crop_variety_id,
              exporter_process_brand_id: package_item.exporter_process_brand_id,
              exporter_process_crop_size_id: pkg_crop_size.id,
              package_group_id: package_item.package_group_id,
              package_group: package_item.package_group,
              quantity: pkg_crop_size.quantity,
              _destroy: package_item._destroy
            });
          });
      });
      return packages_params;
    }
  },
  methods: {
    loadData: async function () {
      await this.getExporterProcessCropCategories();
      await this.getExporterProcessCropVarieties();
      await this.getExporterProcessCropSizes();
      await this.getExporterProcessBrands();
      await this.getExporterProcessCropPackages().then(() => {
        this.newPackage();
        this.getPackages();
      });
      await this.getPackageGroups();
      this.load_general_data = true;
    },
    getContainer: async function () {
      let response = await Requester.container({
        container_id: this.container_id
      });
      this.container = response.data;
      this.max_package_groups = this.container.max_package_groups;
      this.len_package_groups = this.container.package_groups.length;
    },
    newPackage: function () {
      this.new_package = {
        id: null,
        producer_id: this.producer_id,
        organization_id: this.organization_id,
        exporter_process_crop_package_id: this.setAttributeDefault(
          "exporter_process_crop_package_id"
        ),
        exporter_process_crop_category_id: this.setAttributeDefault(
          "exporter_process_crop_category_id"
        ),
        exporter_process_crop_variety_id: this.setAttributeDefault(
          "exporter_process_crop_variety_id"
        ),
        exporter_process_brand_id: this.setAttributeDefault(
          "exporter_process_brand_id"
        ),
        package_group_id: this.setAttributeDefault("package_group_id"),
        package_group: this.setAttributeDefault("package_group"),
        exporter_process_crop_sizes: this.exporter_process_crop_sizes.map(
          exp_pr_crop_size => {
            return {
              id: exp_pr_crop_size.id,
              quantity: null,
              label: exp_pr_crop_size.name
            };
          }
        ),
        _destroy: false
      };
    },
    addPackage: function () {
      if (!this.validate) return;
      let find_package = this.findPackage();

      if (find_package) {
        find_package.exporter_process_crop_sizes =
          this.new_package.exporter_process_crop_sizes;
      } else {
        if (this.max_package_groups > this.len_package_groups) {
          this.packages.push({
            producer_id: this.producer_selected,
            organization_id: this.organization_selected,
            ...this.new_package
          });
          if (this.new_package.package_group_id == null) {
            this.len_package_groups += 1;
          }
        } else {
          alert("No se pueden agregar mas pallets");
        }
      }
      this.newPackage();
      this.editing = false;
    },
    findPackage: function () {
      return this.packages.find(
        package_item =>
          package_item.exporter_process_crop_package_id ==
            this.new_package.exporter_process_crop_package_id &&
          package_item.exporter_process_crop_category_id ==
            this.new_package.exporter_process_crop_category_id &&
          package_item.exporter_process_crop_variety_id ==
            this.new_package.exporter_process_crop_variety_id &&
          package_item.exporter_process_brand_id ==
            this.new_package.exporter_process_brand_id &&
          package_item.package_group_id == this.new_package.package_group_id &&
          package_item.package_group.pg_code ==
            this.new_package.package_group.pg_code
      );
    },
    packageWithAttrsExist: function (new_package_item) {
      let item_existing = this.packages.filter(
        package_item => package_item == new_package_item
      );
      return item_existing.length > 1;
    },
    setAttributeDefault: function (attr) {
      let len = this.packages.length;
      if (attr == "package_group") {
        return len > 0
          ? {
              pg_code: this.packages[len - 1][attr]["pg_code"],
              thermo_code: this.packages[len - 1][attr]["thermo_code"]
            }
          : {
              pg_code: null,
              thermo_code: null
            };
      }
      return len > 0 ? this.packages[len - 1][attr] : null;
    },
    getPackages: async function () {
      let response = await Requester.fetchPackages(
        this.container_id,
        this.producer_id,
        this.organization_id
      );
      this.producer_name = response.data[0].producer?.name;
      this.organization_name = response.data[0].organization?.name;

      this.packages = response.data.map(item => {
        return {
          id: item.id,
          producer_id: this.producer_id,
          organization_id: this.organization_id,
          exporter_process_crop_package_id:
            item.exporter_process_crop_package_id,
          exporter_process_crop_category_id:
            item.exporter_process_crop_category_id,
          exporter_process_crop_variety_id:
            item.exporter_process_crop_variety_id,
          exporter_process_brand_id: item.exporter_process_brand_id,
          package_group_id: item.package_group_id,
          package_group: item.package_group,
          _destroy: false,
          exporter_process_crop_sizes: this.exporter_process_crop_sizes.map(
            exp_pr_crop_size => {
              return {
                id: exp_pr_crop_size.id,
                quantity:
                  exp_pr_crop_size.id == item.exporter_process_crop_size_id
                    ? item.quantity
                    : null,
                label: exp_pr_crop_size.name
              };
            }
          )
        };
      });
      this.producer_organization_selected =
        response.data[0].producer || response.data[0].organization;
    },
    getPackageGroups: async function () {
      let response = await Requester.fetchPackageGroups(this.container_id);

      this.package_groups = response.data;
    },
    getExporterProcessCropCategories: async function () {
      let response = await Requester.fetchExportProcessCropCategories(
        this.agro_exporter_process_id
      );

      this.exporter_process_crop_categories = response.data;
    },
    getExporterProcessCropVarieties: async function () {
      let response = await Requester.fetchExportProcessCropVarieties(
        this.agro_exporter_process_id
      );

      this.exporter_process_crop_varieties = response.data;
    },
    getExporterProcessBrands: async function () {
      let response = await Requester.fetchExportProcessBrands(
        this.agro_exporter_process_id,
        this.container_id
      );

      this.exporter_process_brands = response.data;
    },
    getExporterProcessCropPackages: async function () {
      let response = await Requester.fetchExportProcessCropPackages(
        this.agro_exporter_process_id
      );

      this.exporter_process_crop_packages = response.data;
    },
    getExporterProcessCropSizes: async function () {
      let response = await Requester.fetchExportProcessCropSizes(
        this.agro_exporter_process_id
      );

      this.exporter_process_crop_sizes = response.data;
      if (this.exporter_process_crop_sizes.length == 1) {
        this.exporter_process_crop_sizes[0].name = 'Cantidad';
      }
    },
    getCropCategoryLabel: function (exporter_process_crop_category_id) {
      return this.exporter_process_crop_categories.find(
        exporter_process_crop_category =>
          exporter_process_crop_category.id == exporter_process_crop_category_id
      )?.name;
    },
    getCropPackageLabel: function (exporter_process_crop_package_id) {
      let exporter_process_crop_package =
        this.exporter_process_crop_packages.find(
          exporter_process_crop_package =>
            exporter_process_crop_package.id == exporter_process_crop_package_id
        );
      return {
        name: exporter_process_crop_package?.name,
        weight: exporter_process_crop_package?.gross_weight
      };
    },
    getCropVarietyLabel: function (exporter_process_crop_variety_id) {
      return this.exporter_process_crop_varieties.find(
        exporter_process_crop_variety =>
          exporter_process_crop_variety.id == exporter_process_crop_variety_id
      )?.name;
    },
    getCropSizeLabel: function (exporter_process_crop_size_id) {
      return this.exporter_process_crop_sizes.find(
        exporter_process_crop_size =>
          exporter_process_crop_size.id == exporter_process_crop_size_id
      )?.name;
    },
    getBrandLabel: function (exporter_process_brand_id) {
      return this.exporter_process_brands.find(
        exporter_process_brand =>
          exporter_process_brand.id == exporter_process_brand_id
      )?.name;
    },
    getPackageGroupLabel: function (package_group_id) {
      return this.package_groups.find(
        package_group => package_group.id == package_group_id
      )?.pg_code;
    },
    update: async function () {
      this.proc_save_button = true;
      let response = await Requester.updatePackages(
        this.container_id,
        this.packages_params
      );
      if (response.status == 200) {
        this.registerSuccessNotification({
          title: response.data.success_message
        });
        this.goToShowContainer();
      }
      this.proc_save_button = false;
    },
    removePackage: function (package_item) {
      if (package_item.id) package_item._destroy = true;
      else this.packages.splice(this.packages.indexOf(package_item), 1);
    },
    editPackage: function (package_item) {
      this.new_package = package_item;
      this.editing = true;
    },
    getProducerLabel: function () {
      if (this.producer_name && this.organization_name)
        return `${this.producer_name} (${this.organization_name})`;
      else if (this.producer_name) return this.producer_name;
      else if (this.organization_name) return this.organization_name;

      return "";
    },
    goToShowContainer: function () {
      this.$router.push({
        name: this.routeName("show_container_packages"),
        params: {
          agro_exporter_process_id: this.agro_exporter_process_id,
          container_id: this.container_id,
        }
      });
    }
  }
};
</script>
